import { NetworkService, VectorAIService } from ".";
import {
  applyCollectionFilters,
  getAggregatedFields,
  getAllFields,
  getClusteringFields,
} from "../helper/collection.helper";
import { TryCatchAction } from "../helper/redux.helpers";
import { IAdvanceClusterForm } from "../modals/advance-cluster-aggregation.modal";
import { IPublishAggregationForm } from "../modals/publish-aggregation.modal";
import { IAdvanceClusterFacetFormValue } from "../modals/advance-cluster-facets.modal";
import { store } from "../store";
import {
  CLUSTER_AGGREGATION_ACTION,
  GET_ADVANCE_CLUSTER_AGGREGATION_ACTION,
  GET_ADVANCE_CLUSTER_FACETS_ACTION,
  GET_AGGREGATED_DATA_ACTION,
  GET_CLUSTER_FACETS_ACTION,
  GET_COLLECTIONS_ACTION,
  PUBLISH_AGGREGATION_ACTION,
  SELECT_COLLECTION_ACTION,
  AGGREGATION_PAGINATED_ACTION,
  RECOMMENDATION_SEARCH_RESULTS_ACTION,
  RECOMMENDATION_CONFIG_ACTION,
  SINGLE_RECOMMENDATION_ACTION,
} from "../store/collection/collection.action";
import {
  selectCollectionAggregatedData,
  selectSelectedCollection,
} from "../store/collection/collection.selector";
import {
  IAggregatedField,
  IAggregationQuery,
  ICollection,
  ICollectionAggregateData,
  ICollectionSchemaResponse,
  IListCollectionResponse,
  IRecommendationSearchResults,
  ISingleProductRecommendationResponse,
} from "../types";
import { ApiError } from "./network.service";
import VectorAIBase from "./vectorai-base.service";

class CollectionService extends VectorAIBase {
  @TryCatchAction(GET_COLLECTIONS_ACTION)
  public async listCollections(form: { username: string; apiKey: string }) {
    const response = await NetworkService.get(
      `/project/list_collections?username=${form.username}&api_key=${form.apiKey}`
    );
    this.setAuth(form.apiKey, form.username);
    if (response?.length) {
      this.selectCollection(response[0]);
      // this.selectCollection("ecommerce");
    }

    return response;
  }

  public async updateCollections() {
    const response = await this.get<IListCollectionResponse>(
      "/project/list_collections"
    );
    if (!response) {
      throw new ApiError("Collections not found");
    }
    store.dispatch(GET_COLLECTIONS_ACTION.success(response));
    store.dispatch(GET_CLUSTER_FACETS_ACTION.reset());
    store.dispatch(GET_ADVANCE_CLUSTER_AGGREGATION_ACTION.reset());
    store.dispatch(GET_ADVANCE_CLUSTER_FACETS_ACTION.reset());
  }

  @TryCatchAction(SELECT_COLLECTION_ACTION)
  public async selectCollection(collectionName: string): Promise<ICollection> {
    const schema = await this.getCollectionSchema(collectionName);
    if (!schema) {
      throw new ApiError("Schema fetching failed");
    }
    const aggregationQuery = await VectorAIService.randomAggregationQuery(
      schema
    );

    store.dispatch(GET_CLUSTER_FACETS_ACTION.reset());
    store.dispatch(GET_ADVANCE_CLUSTER_AGGREGATION_ACTION.reset());
    store.dispatch(GET_ADVANCE_CLUSTER_FACETS_ACTION.reset());
    store.dispatch(RECOMMENDATION_SEARCH_RESULTS_ACTION.reset());

    this.aggregate(collectionName, aggregationQuery, 20, 1, true);
    store.dispatch(
      RECOMMENDATION_CONFIG_ACTION({
        approx: 0,
        hundred_scale: false,
        include_count: true,
        include_vector: false,
        min_score: 0,
        metric: "cosine",
        page: 1,
        page_size: 10,
        search_field: "",
        sum_fields: true,
        document_id: "",
        type: {
          label: "Single Document",
          value: "SINGLE_PRODUCT_RECOMMENDATION",
        },
        vector_operation: {
          label: "Mean",
          value: "mean",
        },
        setup: true,
        document_ids: [],
        positive_document_ids: [],
        negative_document_ids: [],
      })
    );
    store.dispatch(SINGLE_RECOMMENDATION_ACTION.reset());
    this.clusterAggregation(collectionName, aggregationQuery);
    const aggregatedFields = getAggregatedFields(aggregationQuery, schema);
    return {
      name: collectionName,
      schema,
      aggregationQuery,
      aggregatedFields,
      aggregationAscending: true,
      aggregationSortBy: {
        agg: "sum",
        field: "frequency",
        name: "frequency",
        type: "numeric",
      },
      clusteringFields: getClusteringFields(aggregatedFields),
    };
  }

  public async getCollectionSchema(collectionName: string) {
    return await this.get<ICollectionSchemaResponse>(
      "/project/collection_schema",
      {
        collection_name: collectionName,
      }
    );
  }

  @TryCatchAction(GET_AGGREGATED_DATA_ACTION)
  public async aggregate(
    collectionName: string,
    aggregationQuery: IAggregationQuery,
    pageSize: number = 10,
    page: number = 1,
    asc: boolean = true
  ) {
    const response: ICollectionAggregateData[] | undefined = await this.post(
      "/collection/aggregate",
      {
        collection_name: collectionName,
        aggregation_query: aggregationQuery,
        page_size: pageSize,
        page,
        asc,
        flatten: true,
      }
    );

    if (response) {
      this.handleAggregationPagination(response, 10, 1);
    }
    return response;
  }

  public async updateAggregationQuery(aggregationQuery: IAggregationQuery) {
    const collection = selectSelectedCollection(store.getState()).data;
    if (!collection) {
      throw new ApiError("Collection not found");
    }
    this.aggregate(collection.name, aggregationQuery);
    this.clusterAggregation(collection.name, aggregationQuery);
    store.dispatch(GET_ADVANCE_CLUSTER_AGGREGATION_ACTION.reset());
    const aggregatedFields = getAggregatedFields(
      aggregationQuery,
      collection.schema
    );
    store.dispatch(
      SELECT_COLLECTION_ACTION.success({
        ...collection,
        aggregatedFields,
        aggregationQuery,
        clusteringFields: getClusteringFields(aggregatedFields),
      })
    );
  }

  public updateAggregationSortBy(aggregationSortBy: IAggregatedField) {
    const collection = selectSelectedCollection(store.getState()).data;
    if (!collection) {
      throw new ApiError("Collection not found");
    }
    const aggregatedData = selectCollectionAggregatedData(store.getState())
      .data;
    store.dispatch(
      SELECT_COLLECTION_ACTION.success({
        ...collection,
        aggregationSortBy,
      })
    );

    store.dispatch(
      GET_AGGREGATED_DATA_ACTION.success(
        applyCollectionFilters(aggregatedData, aggregationSortBy)
      )
    );
  }

  public async updateAscending(aggregationAscending: boolean) {
    const collection = selectSelectedCollection(store.getState()).data;
    if (!collection) {
      throw new ApiError("Collection not found");
    }
    store.dispatch(
      SELECT_COLLECTION_ACTION.success({
        ...collection,
        aggregationAscending,
      })
    );

    const response: any = await this.post("/collection/aggregate", {
      collection_name: collection.name,
      aggregation_query: collection.aggregationQuery,
      page_size: 20,
      page: 1,
      asc: aggregationAscending,
    });

    this.handleAggregationPagination(response, 10, 1);

    store.dispatch(GET_AGGREGATED_DATA_ACTION.success(response));
  }

  @TryCatchAction(PUBLISH_AGGREGATION_ACTION)
  public async publishAggregation(value: IPublishAggregationForm) {
    const collection = selectSelectedCollection(store.getState()).data;

    if (!collection) {
      throw new ApiError("Collection not found");
    }

    const response = await this.post("/collection/publish_aggregation", {
      source_collection: collection.name,
      aggregation_query: collection.aggregationQuery,
      dest_collection: value.destCollection,
      aggregation_name: value.aggregationName,
      description: value.description,
      date_field: value.dateField,
      refresh_time: `${value.refreshTime}s`,
      start_immediately: true,
    });

    return response;
  }

  @TryCatchAction(CLUSTER_AGGREGATION_ACTION)
  public async clusterAggregation(
    collectionName: string,
    aggregationQuery: IAggregationQuery,
    pageSize: number = 20,
    page: number = 1,
    asc: boolean = true
  ) {
    const response = await this.post("/collection/cluster_aggregate", {
      collection_name: collectionName,
      aggregation_query: aggregationQuery,
      page_size: pageSize,
      page,
      asc,
    });

    return response;
  }

  @TryCatchAction(GET_CLUSTER_FACETS_ACTION)
  public async getClusteringFacets(form: {
    facetsFields: string[];
    dateInterval: string;
    page: number;
    pageSize: number;
    asc: boolean;
  }) {
    const collection = selectSelectedCollection(store.getState());
    if (!collection.data) {
      throw new ApiError("Collection not found");
    }
    return await this.get("/collection/cluster_facets", {
      collection_name: collection.data.name,
      date_interval: form.dateInterval,
      facets_fields: form.facetsFields,
      page_size: form.pageSize,
      page: form.page,
      asc: form.asc,
    });
  }

  @TryCatchAction(GET_ADVANCE_CLUSTER_FACETS_ACTION)
  public async getAdvanceClusteringFacets(form: IAdvanceClusterFacetFormValue) {
    const collection = selectSelectedCollection(store.getState());
    if (!collection.data) {
      throw new ApiError("Collection not found");
    }
    return await this.get("/collection/advanced_cluster_facets", {
      vector_field: form.vectorField,
      alias: form.alias,
      facets_fields: form.facetsFields,
      page_size: form.pageSize,
      page: form.page,
      asc: form.asc,
      date_interval: form.dateInterval,
      collection_name: collection.data.name,
    });
  }

  @TryCatchAction(GET_ADVANCE_CLUSTER_AGGREGATION_ACTION)
  public async getAdvanceClusterAggregation(form: IAdvanceClusterForm) {
    const collection = selectSelectedCollection(store.getState());
    if (!collection.data) {
      throw new ApiError("Collection not found");
    }

    const response = await this.post("/collection/advanced_cluster_aggregate", {
      collection_name: collection.data.name,
      vector_field: form.vectorField,
      aggregation_query: collection.data.aggregationQuery,
      page: form.page,
      page_size: form.pageSize,
      asc: form.asc,
      alias: form.alias,
    });
    return response;
  }

  public handleAggregationPagination(
    data: ICollectionAggregateData[],
    pageSize: number,
    page: number
  ) {
    const start = (page - 1) * pageSize;
    const end = start + pageSize;
    const paginatedData = (data || []).slice(start, end);
    store.dispatch(
      AGGREGATION_PAGINATED_ACTION({
        currentPage: page,
        data: paginatedData,
        pageSize,
        total: data.length,
      })
    );
  }

  public async encodeText(text: string): Promise<number[]> {
    const collection = selectSelectedCollection(store.getState());

    if (!collection) {
      throw new ApiError("Collection not found");
    }

    const response: number[] | undefined = await this.get(
      "/collection/encode_text",
      {
        text,
        collection_name: collection.data?.name,
      }
    );
    if (!response) {
      throw new ApiError("Something went wrong");
    }

    return response;
  }

  @TryCatchAction(RECOMMENDATION_SEARCH_RESULTS_ACTION)
  public async searchTextDocuments(
    text: string,
    search_fields: string,
    sum_fields: boolean = true,
    page_size: number = 5,
    page: number = 1,
    metric: string = "cosine",
    min_score: number = 0,
    include_vector: boolean = false,
    include_count: boolean = true
  ): Promise<IRecommendationSearchResults> {
    const collection = selectSelectedCollection(store.getState());
    if (!collection.data) {
      throw new ApiError("Collection not found");
    }
    const vector = await this.encodeText(text);
    const advanced_search_query = {
      [search_fields.replace("_vector_", "")]: {
        vector: vector,
        fields: [search_fields],
      },
    };

    const response:
      | ISingleProductRecommendationResponse
      | undefined = await this.post("/collection/advanced_search", {
      collection_name: collection.data?.name,
      multivector_query: advanced_search_query,
      facets: [],
      filters: [],
      sum_fields: sum_fields,
      metric: metric,
      min_score: min_score,
      page: page,
      page_size: page_size,
      include_vector: include_vector,
      include_count: include_count,
      include_facets: false,
    });
    if (!response) {
      throw new ApiError("Something went wrong");
    }
    return {
      currentPage: page,
      pageSize: page_size,
      data: response.results,
      total: response.count,
      fields: getAllFields(collection.data?.schema || {}),
    };
  }
}

export default new CollectionService();
